import { useMemo } from "react"

import { CACHE_PROFILES } from "@/config/query-cache"
import { normalizeRunRowSummary } from "@/runs/values"
import {
  transport,
  useTransportCollectionQuery,
  useTransportMutation,
  useTransportPaginatedQuery,
  useTransportQuery,
  useTransportQueryUtils,
} from "@/transport"

import { getMetadataFilterValidationError } from "@/lib/metadataFilters"
import type { ListReportsRequest } from "@/types_api/backend/transport/list_reports_request"
import type { ListReportsResponse } from "@/types_api/backend/transport/list_reports_response"
import type { ReportRowsResponse } from "@/types_api/backend/transport/report_rows_response"
import type { RunSummaryRowResource } from "@/types_api/backend/transport/run_rows_response"
import type { UploadedConversationReportRowsResponse } from "@/types_api/backend/transport/uploaded_conversation_report_rows_response"

import {
  clampUploadedConversationReportRowsLimit,
  isReportTransportQueryLoading,
  normalizeReportResource,
  normalizeReportRows,
  type UploadedConversationReportRowsTransportFilters,
} from "./reportTransportNormalization"

export {
  buildUploadedConversationReportRowsTransportFilters,
  getUploadedConversationSimulationOutputIds,
  isReportTransportQueryLoading,
  mergeReportRowMetricDetails,
  normalizeReportResource,
  resolveReportRowsSimulationOutputIds,
} from "./reportTransportNormalization"

const REPORTS_LIST_PAGE_LIMIT = 100
const REPORT_ROWS_PAGE_LIMIT = 100

const REPORTS_LIST_REQUEST = {
  limit: REPORTS_LIST_PAGE_LIMIT,
} satisfies ListReportsRequest
type ReportResource = ListReportsResponse["reports"][number]

interface ReportRowsTransportOptions {
  enabled?: boolean
  includeMetricDetails?: boolean
  metricIds?: string[]
  /** Optional subset filter; omit/null to span the report's full run_ids. */
  simulationOutputIds?: string[] | null
  staleTime?: number
}

interface UploadedConversationReportRowsTransportOptions {
  enabled?: boolean
  filters?: UploadedConversationReportRowsTransportFilters
  limit?: number
  simulationOutputIds?: string[] | null
  staleTime?: number
}

export function useReportsListTransport(options?: { enabled?: boolean }) {
  const enabled = options?.enabled ?? true
  const query = useTransportCollectionQuery<"listReports", ReportResource>({
    operation: transport.collections.listReports,
    request: REPORTS_LIST_REQUEST,
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
    },
  })

  return {
    ...query,
    data: query.data?.items.map(normalizeReportResource),
    isLoading: isReportTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function useReportTransport(
  reportId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = Boolean(reportId) && (options?.enabled ?? true)
  const query = useTransportQuery({
    operation: transport.operations.getReport,
    request: { report_id: reportId ?? "" },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
    },
  })

  // Memoize on the stable react-query report reference. normalizeReportResource
  // builds a fresh object (incl. a new metric_config) on every call, so without
  // this each render would yield a new identity and any effect keyed on the
  // normalized data would re-fire indefinitely.
  const data = useMemo(
    () =>
      query.data?.report
        ? normalizeReportResource(query.data.report)
        : undefined,
    [query.data?.report],
  )

  return {
    ...query,
    data,
    isLoading: isReportTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function usePublicReportTransport(
  reportId: string | null | undefined,
  options?: { enabled?: boolean; staleTime?: number },
) {
  const enabled = Boolean(reportId) && (options?.enabled ?? true)
  const query = useTransportQuery({
    operation: "getPublicReport",
    request: { report_id: reportId ?? "" },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
    },
  })

  const data = useMemo(
    () =>
      query.data?.report
        ? normalizeReportResource(query.data.report)
        : undefined,
    [query.data?.report],
  )

  return {
    ...query,
    data,
    isLoading: isReportTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

function combineRowsPages<TRow>(
  pages: {
    rows: TRow[]
    next_cursor?: string | null
  }[],
) {
  return {
    rows: pages.flatMap((page) => page.rows),
    next_cursor: pages.at(-1)?.next_cursor ?? null,
  }
}

function combineReportRowsPages(pages: ReportRowsResponse[]) {
  return combineRowsPages(pages) satisfies ReportRowsResponse
}

function combineUploadedConversationReportRowsPages(
  pages: UploadedConversationReportRowsResponse[],
) {
  return combineRowsPages(
    pages,
  ) satisfies UploadedConversationReportRowsResponse
}

function getNextReportRowsRequest<
  TPage extends {
    next_cursor?: string | null
  },
  TRequest extends {
    cursor?: string | null
    limit?: number
  },
>(page: TPage, previousRequest: TRequest | undefined) {
  return page.next_cursor
    ? {
        ...(previousRequest ?? ({} as TRequest)),
        cursor: page.next_cursor,
      }
    : null
}

export function useReportRowsTransport(
  reportId: string | null | undefined,
  options?: ReportRowsTransportOptions,
) {
  const enabled = Boolean(reportId) && (options?.enabled ?? true)
  const query = useTransportPaginatedQuery({
    combinePages: combineReportRowsPages,
    getPageItemCount: (page) => page.rows.length,
    getNextRequest: getNextReportRowsRequest,
    operation: "getReportRows",
    request: {
      report_id: reportId ?? "",
      limit: REPORT_ROWS_PAGE_LIMIT,
      metric_ids: options?.metricIds ?? [],
      include_metric_details: options?.includeMetricDetails ?? false,
      // Omitted (undefined) when unset so the default request spans full run_ids.
      simulation_output_ids: options?.simulationOutputIds ?? undefined,
    },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
    },
  })

  return {
    ...query,
    data: normalizeReportRows(query.data?.rows),
    isLoading: isReportTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function usePublicReportRowsTransport(
  reportId: string | null | undefined,
  options?: ReportRowsTransportOptions,
) {
  const enabled = Boolean(reportId) && (options?.enabled ?? true)
  const query = useTransportPaginatedQuery({
    combinePages: combineReportRowsPages,
    getPageItemCount: (page) => page.rows.length,
    getNextRequest: getNextReportRowsRequest,
    operation: "getPublicReportRows",
    request: {
      report_id: reportId ?? "",
      limit: REPORT_ROWS_PAGE_LIMIT,
      metric_ids: options?.metricIds ?? [],
      include_metric_details: options?.includeMetricDetails ?? false,
      // Omitted (undefined) when unset so the default request spans full run_ids.
      simulation_output_ids: options?.simulationOutputIds ?? undefined,
    },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
    },
  })

  return {
    ...query,
    data: normalizeReportRows(query.data?.rows),
    isLoading: isReportTransportQueryLoading({
      data: query.data,
      enabled,
      error: query.error,
      isLoading: query.isLoading,
    }),
  }
}

export function useUploadedConversationReportRowsTransport(
  options?: UploadedConversationReportRowsTransportOptions,
) {
  const validationError = getMetadataFilterValidationError(
    options?.filters?.metadata_filters,
  )
  const enabled = (options?.enabled ?? true) && !validationError
  const limit = clampUploadedConversationReportRowsLimit(options?.limit)
  const query = useTransportPaginatedQuery({
    combinePages: combineUploadedConversationReportRowsPages,
    getPageItemCount: (page) => page.rows.length,
    getNextRequest: getNextReportRowsRequest,
    operation: "getUploadedConversationReportRows",
    request: validationError
      ? null
      : {
          limit,
          simulation_output_ids: options?.simulationOutputIds?.length
            ? options.simulationOutputIds
            : undefined,
          filters: options?.filters ?? {},
        },
    options: {
      ...CACHE_PROFILES.lookup,
      enabled,
      refetchOnWindowFocus: false,
      staleTime: options?.staleTime ?? CACHE_PROFILES.lookup.staleTime,
    },
  })

  return {
    ...query,
    error: validationError ? new Error(validationError) : query.error,
    isError: Boolean(validationError) || query.isError,
    data: validationError
      ? undefined
      : query.data?.rows.map((row) =>
          normalizeRunRowSummary(row as unknown as RunSummaryRowResource),
        ),
    isLoading:
      !validationError &&
      isReportTransportQueryLoading({
        data: query.data,
        enabled,
        error: query.error,
        isLoading: query.isLoading,
      }),
  }
}

export function useSaveReportTransport() {
  return useTransportMutation("saveReport")
}

export function useUpdateReportTransport() {
  return useTransportMutation("updateReport")
}

export function useDeleteReportTransport(options?: {
  onSuccess?: () => void | Promise<void>
}) {
  return useTransportMutation(transport.operations.deleteReport, options)
}

export function useSetReportVisibilityTransport(options?: {
  onSuccess?: () => void | Promise<void>
}) {
  return useTransportMutation(transport.operations.setReportVisibility, options)
}

export function useReportsTransportInvalidation() {
  const transportUtils = useTransportQueryUtils()

  return useMemo(
    () => ({
      async invalidateList() {
        await transportUtils.invalidate(transport.resources.reports)
      },
      async invalidateReport(reportId: string) {
        await Promise.all([
          transportUtils.invalidate(transport.operations.getReport, {
            report_id: reportId,
          }),
          transportUtils.invalidate("getPublicReport", { report_id: reportId }),
          transportUtils.invalidatePaginatedOperation("getReportRows"),
          transportUtils.invalidatePaginatedOperation("getPublicReportRows"),
        ])
      },
    }),
    [transportUtils],
  )
}
